import { isApiError, isNetworkError } from "../api/errors";
import type { Endpoints } from "../api/endpoints";
import { errorMessage } from "../lib/errorMessage";
import { db, type QueuedSale } from "./db";
import { listQueue, refreshQueueCounts } from "./saleQueue";
import { setSyncState } from "./syncState";

/** Replays the offline queue (§16.1): oldest first, one sale at a time, through
 * POST /sales/sync. The server de-duplicates by clientTxnId, so a retry after a dropped
 * response is safe.
 * - network failure → stop the run, keep everything pending, retry later;
 * - a business rejection for one sale → mark it failed with the message, carry on;
 * - any other server error (5xx, 401) → stop, keep pending. */
export const OFFLINE_MESSAGE = "Cannot reach server — will retry automatically";

export type SyncOutcome = { synced: number; failed: number; stopped: string | null };

async function withLock<T>(fn: () => Promise<T>): Promise<T | null> {
  // One replayer across tabs; a second tab simply skips this round.
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (!locks?.request) return fn();
  return locks.request("barapos-sync", { ifAvailable: true }, async (lock) => (lock ? fn() : null));
}

export async function runSync(api: Endpoints, branchId: number | null, onSynced?: () => void): Promise<SyncOutcome> {
  const result = await withLock(async (): Promise<SyncOutcome> => {
    const out: SyncOutcome = { synced: 0, failed: 0, stopped: null };
    const queue = (await listQueue()).filter((q) => q.status === "pending" && (branchId === null || q.branchId === branchId));
    for (const item of queue) {
      try {
        const [res] = await api.sales.sync([item.payload]);
        if (res?.ok) {
          await (await db()).delete("saleQueue", item.id);
          out.synced += 1;
        } else {
          await (await db()).put("saleQueue", { ...item, status: "failed", lastError: res?.message ?? "The server refused this sale." } satisfies QueuedSale);
          out.failed += 1;
        }
      } catch (e) {
        out.stopped = isNetworkError(e) ? OFFLINE_MESSAGE : isApiError(e) && e.status === 401 ? "Sign in again to sync." : errorMessage(e, "The server couldn't sync right now; it will retry automatically.");
        break;
      }
    }
    return out;
  });
  await refreshQueueCounts();
  const r = result ?? { synced: 0, failed: 0, stopped: null };
  setSyncState({ message: r.stopped ?? (r.synced ? `${r.synced} offline sale${r.synced === 1 ? "" : "s"} synced` : null) });
  if (r.synced || r.failed) onSynced?.();
  return r;
}

/** Retry a failed sale: back to pending, then sync. */
export async function retryQueued(id: string): Promise<void> {
  const d = await db();
  const item = await d.get("saleQueue", id);
  if (item) await d.put("saleQueue", { ...item, status: "pending", lastError: null });
  await refreshQueueCounts();
}

/** Discard a failed sale, keeping it in a local archive (plan: never silently lose money). */
export async function discardQueued(id: string, reason: string, by: string): Promise<void> {
  const d = await db();
  const item = await d.get("saleQueue", id);
  if (!item) return;
  const tx = d.transaction(["saleQueue", "saleArchive"], "readwrite");
  await tx.objectStore("saleArchive").put({ ...item, discardedAt: new Date().toISOString(), discardReason: reason, discardedBy: by });
  await tx.objectStore("saleQueue").delete(id);
  await tx.done;
  await refreshQueueCounts();
}

export async function listArchive() {
  return (await (await db()).getAll("saleArchive")).sort((a, b) => b.discardedAt.localeCompare(a.discardedAt));
}
